/**
 * Phosphor persistence as a two-target feedback loop.
 *
 * Runs at buffer resolution with nearest filtering so a trail stays pixel aligned with
 * the sprite that made it. The HUD is a DOM layer above the canvas and never enters this
 * buffer, so nothing here can smear the UI (§11.3).
 *
 * Off at §14.6 rung 6 (AMD-2).
 */

import * as THREE from 'three';
import { PERSISTENCE_FRAG } from '../shaders/persistence';
import { FullscreenPass, clearTarget, createTarget } from './FullscreenPass';

export class Persistence {
  private targets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private index = 0;
  private readonly pass: FullscreenPass;
  private readonly type: THREE.TextureDataType;
  private primed = false;

  constructor(width: number, height: number, type: THREE.TextureDataType) {
    this.type = type;
    this.targets = [createTarget(width, height, { type }), createTarget(width, height, { type })];

    this.pass = new FullscreenPass(PERSISTENCE_FRAG, {
      tCurrent: { value: null },
      tPrev: { value: null },
      uPersistence: { value: 0.5 },
      // Green phosphor outlives red, red outlives blue. Small differences, but they turn
      // a grey smear into a coloured wake.
      uChromaDecay: { value: new THREE.Vector3(0.965, 0.992, 0.938) },
      uTrailSaturation: { value: 1.1 },
      uFloor: { value: 0.004 },
    });
  }

  /** The accumulated afterglow. The composite maxes this against the live frame. */
  get texture(): THREE.Texture {
    return this.targets[this.index].texture;
  }

  set persistence(value: number) {
    this.pass.uniforms.uPersistence.value = Math.max(0, Math.min(0.995, value));
  }

  setChromaDecay(r: number, g: number, b: number): void {
    (this.pass.uniforms.uChromaDecay.value as THREE.Vector3).set(r, g, b);
  }

  set trailSaturation(value: number) {
    this.pass.uniforms.uTrailSaturation.value = value;
  }

  resize(width: number, height: number): void {
    if (width === this.targets[0].width && height === this.targets[0].height) return;
    this.targets[0].dispose();
    this.targets[1].dispose();
    this.targets = [createTarget(width, height, { type: this.type }), createTarget(width, height, { type: this.type })];
    this.primed = false;
  }

  /** Wipes both buffers. Called on resize, on run reset and before a capture. */
  reset(renderer: THREE.WebGLRenderer): void {
    clearTarget(renderer, this.targets[0]);
    clearTarget(renderer, this.targets[1]);
    this.primed = true;
  }

  render(renderer: THREE.WebGLRenderer, current: THREE.Texture): void {
    if (!this.primed) this.reset(renderer);

    const prev = this.targets[this.index];
    const next = this.targets[1 - this.index];

    this.pass.uniforms.tCurrent.value = current;
    this.pass.uniforms.tPrev.value = prev.texture;
    this.pass.render(renderer, next);

    this.index = 1 - this.index;
  }

  dispose(): void {
    this.targets[0].dispose();
    this.targets[1].dispose();
    this.pass.dispose();
  }
}
